import {Clipboard} from "@angular/cdk/clipboard";
import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {FormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatSuffix} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatInput} from "@angular/material/input";
import {MatSidenav} from "@angular/material/sidenav";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {MatTooltip} from "@angular/material/tooltip";
import {Router} from "@angular/router";
import {LandlordsService, SUPPORTED_OFFERING_TYPES} from "@core-services/landlords.service";
import {OfferingConfigCode, QrCodeDestination} from "@core/model/landlord.model";
import {LocationOffering, PageOffering} from "@core/model/side-nav.model";
import {environment} from "@env/environment";
import {ConsoleContextService} from "@security/console-context.service";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";
import {equalsIgnoreCase, isNotEmpty} from "@ui-kit/framework/util/static/string-util";
import {PaginatorModule} from "primeng/paginator";
import QRCode from "qrcode";
import {lastValueFrom} from "rxjs";
import {SurveyService} from "../../../survey.service";

@Component({
  selector: 'app-survey-qrcode-configuration',
  standalone: true,
  imports: [
    FlexModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatSuffix,
    MatTab,
    MatTabGroup,
    MatTooltip,
    PaginatorModule,
    FormsModule,
    MatIconButton
  ],
  templateUrl: './survey-qrcode-configuration.component.html'
})
export class SurveyQRCodeConfigurationComponent implements OnInit, AfterViewInit {

  @Input() offering!: PageOffering;
  @Input() surveyCode!: string;

  @ViewChild('rightSidenav') sidenav!: MatSidenav;

  private _offeringUuid!: string;
  private locationOffering!: LocationOffering;

  protected universalAppLink!: string;
  protected qrCodeImageSrc?: string;
  protected qrCodeUrl: string | null = null;

  constructor(public loader: LoadingService,
              public dateUtils: DateUtilsService,
              public utils: UtilsService,
              private surveyService: SurveyService,
              private clipboard: Clipboard,
              private currentContext: ConsoleContextService,
              private router: Router,
              private landlordService: LandlordsService,
              private snackbarService: SnackbarService) {
  }

  ngOnInit(): void {
    this.locationOffering = this.currentContext.getCurrentLocationOffering(this.offering?.uuid!)!;
    this._offeringUuid = this.locationOffering.offeringUuid;
    this.universalAppLink = this.buildUniversalAppLink(); //removed in master
    this.generateQRCode();
  }

  ngAfterViewInit(): void {
    this.qrCodeUrl = this.getApplicationFormQrCodeDestination();
    if (this.sidenav) {
      setTimeout(() => this.sidenav.open(), 200);
    }
  }

  get qrCodeUrlFromContext(): string | null {
    return this.getApplicationFormQrCodeDestination();
  }

  get canUpdateCustomUQrUrl(): boolean {
    return isNotEmpty(this.qrCodeUrl) && !equalsIgnoreCase(this.qrCodeUrl, this.qrCodeUrlFromContext);
  }

  protected testExternalUrl(): void {
    if (this.qrCodeUrl) {
      //sanitize url for safety before opening and parse string as valid url
      const url = new URL(this.qrCodeUrl);
      window.open(url, '_blank');
    } else {
      this.utils.openSnackBar("No valid url to test. Please update the url first.")
    }
  }

  protected updateQrCodeUrl(): void {
    const currentOffering = this.currentContext.getCurrentLocationOffering(this.locationOffering.uuid);
    const updateRequest: QrCodeDestination = {
      formCode: this.surveyCode,
      mode: OfferingConfigCode.SURVEY_QR_CODE_DESTINATION,
      destination: this.qrCodeUrl
    }

    lastValueFrom(this.landlordService.updateSurveyQrCodeUrl(currentOffering!, updateRequest))
      .then(() => {
        // return lastValueFrom(this.landlordService.getOfferingsForLocation(this.currentContext.getCurrentLocation()!.uuid, SUPPORTED_OFFERING_TYPES))
        return Promise.resolve(lastValueFrom(this.landlordService.getOfferingsForLocation(this.currentContext.getCurrentLocation()!.uuid, SUPPORTED_OFFERING_TYPES)));
      })
      .then((res: LocationOffering[]) => {
        const updatedOffering = res.find(offering => offering.offeringUuid === this._offeringUuid);
        this.currentContext.updateLocationOfferings(updatedOffering!);
        this.utils.openSnackBar("QR code url updated successfully");
      });
  }

  protected removeCustomUrl(): void {
    this.qrCodeUrl = null;
    this.updateQrCodeUrl();
  }


  copyEmbedCode() {
    const locationOffering = this.currentContext.getCurrentLocation()?.locationOfferings
      ?.find(offering => offering.offeringUuid === this._offeringUuid)!;

    const script: HTMLScriptElement = document.createElement('script');
    script.type = 'text/javascript';
    script.src = environment.apiSurveyForm;

    const formEl: HTMLElement = document.createElement('nter-survey-form');
    formEl.setAttribute('location-code', locationOffering.locationCode);
    formEl.setAttribute('offering-code', locationOffering.offeringCode!);
    formEl.setAttribute('embed-key', locationOffering.embedKey!);
    formEl.setAttribute('code', this.surveyCode!);

    const stringToCopy = script.outerHTML + '\n' + formEl.outerHTML;
    if (this.clipboard.copy(stringToCopy)) {
      this.snackbarService.openSnackBar('Embed code copied to clipboard!');
      return;
    }

    //if the clipboard.copy fails, open a modal with the above html code in a textarea for manual copying
    this.snackbarService.openSnackBar(stringToCopy);
  }

  copyHostedFormLink() {
    if (this.clipboard.copy(this.universalAppLink)) {
      this.snackbarService.openSnackBar('Hosted form link copied to clipboard!');
      return;
    }

    //if the clipboard.copy fails, open a modal with the above html code in a textarea for manual copying
    this.snackbarService.openSnackBar(this.universalAppLink);
  }


  downloadQRCodeImage() {
    if (this.qrCodeImageSrc) {
      const link = document.createElement('a');
      link.download = 'qr-code.png';
      link.href = this.qrCodeImageSrc;
      link.click();
    } else {
      console.error('QR code image source is undefined');
      this.snackbarService.openSnackBar('QR image could not be downloaded');
      return;
    }
  }

  private buildUniversalAppLink(): string {
    return `${environment.apiPortal}/${this.locationOffering.locationCode}/${this.locationOffering.offeringCode}/survey/${this.surveyCode}?embedKey=${this.locationOffering.embedKey}`;
  }

  private generateQRCode() {
    QRCode.toDataURL(this.universalAppLink)
      .then(url => {
        this.qrCodeImageSrc = url;
      })
      .catch(err => {
        console.error(err);
        this.snackbarService.openSnackBar('Could not generate QR code!');
      })
  }

  private getApplicationFormQrCodeDestination(): string | null {
    const offering = this.currentContext.getCurrentLocation()?.locationOfferings
      ?.find((offering: any) => offering.offeringUuid === this._offeringUuid)!;
    if (!!offering.surveyQrCodeDestinations && offering.surveyQrCodeDestinations.length > 0) {
      const formDestination = offering.surveyQrCodeDestinations
        .find(f => f.formCode == this.surveyCode);
      return formDestination ? formDestination.destination! : null;
    }
    return null;
  }


}
